import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

const Juridique = ({ clientId }) => {
  const [activeTab, setActiveTab] = useState('analyse'); // 'analyse' | 'generateur'
  const [rightTab, setRightTab] = useState('actes'); // 'actes' | 'chat'
  
  // States Analyse
  const [syntheses, setSyntheses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedSynthese, setSelectedSynthese] = useState(null);

  // States Générateur
  const [selectedTemplate, setSelectedTemplate] = useState('non_remuneration');
  const [instructions, setInstructions] = useState('');
  const [customParams, setCustomParams] = useState({
    nouveau_siege: '',
    exercice_clos: '',
    capital_nouveau: ''
  });
  const [generating, setGenerating] = useState(false);
  const [generatedActe, setGeneratedActe] = useState('');
  const [acteNom, setActeNom] = useState('');
  
  // Historique des actes générés
  const [savedActes, setSavedActes] = useState([]);
  const [loadingActes, setLoadingActes] = useState(false);
  const [selectedSavedActe, setSelectedSavedActe] = useState(null);

  // States Chat Assistant
  const [chatMessages, setChatMessages] = useState([
    { role: 'ai', text: 'Bonjour ! Je suis votre conseiller juridique IA. Comment puis-je vous aider ou vous conseiller dans la rédaction de vos documents d\'entreprise ?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const chatEndRef = useRef(null);

  // Auto-scroll pour le chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatLoading]);

  // Charger les données
  const fetchSyntheses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/juridique/syntheses');
      if (res && res.syntheses) {
        setSyntheses(res.syntheses);
      }
    } catch (err) {
      console.error('Erreur chargement syntheses juridiques:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedActes = async () => {
    setLoadingActes(true);
    try {
      const res = await api.get('/juridique/actes');
      if (res && res.success && res.actes) {
        setSavedActes(res.actes);
      }
    } catch (err) {
      console.error('Erreur chargement actes sauvegardés:', err);
    } finally {
      setLoadingActes(false);
    }
  };

  useEffect(() => {
    fetchSyntheses();
    fetchSavedActes();
  }, [clientId]);

  // Upload et Analyse
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await fetch('/juridique/analyser', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        fetchSyntheses();
        if (result.resultat) {
          setSelectedSynthese(result.resultat);
        }
      } else {
        const error = await response.json();
        alert(`Erreur d'analyse : ${error.detail || 'Fichier invalide ou trop volumineux'}`);
      }
    } catch (err) {
      console.error('Erreur upload juridique:', err);
      alert("Erreur lors de l'envoi du document.");
    } finally {
      setUploading(false);
    }
  };

  // Génération d'acte
  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setGeneratedActe('');
    setSelectedSavedActe(null);
    
    try {
      const res = await api.post('/juridique/generer-acte', {
        type_document: selectedTemplate,
        instructions: instructions,
        parametres: customParams
      });

      if (res && res.success && res.acte) {
        setGeneratedActe(res.acte);
        setActeNom(res.nom_defaut || 'acte_juridique.txt');
        
        // Mettre automatiquement à jour le contexte dans le chat
        setChatMessages(prev => [
          ...prev,
          { role: 'ai', text: `J'ai rédigé un brouillon de **${selectedTemplate === 'non_remuneration' ? 'PV de non-rémunération' : selectedTemplate === 'siege_social' ? 'PV de transfert de siège' : selectedTemplate === 'approbation_comptes' ? 'PV d\'approbation des comptes' : selectedTemplate === 'capital' ? 'PV d\'augmentation de capital' : 'Document sur mesure'}**. N'hésitez pas à me demander des modifications sur le texte à gauche !` }
        ]);
        setRightTab('chat'); // Basculer sur le chat pour guider l'utilisateur
      } else {
        alert("Erreur lors de la génération du document.");
      }
    } catch (err) {
      console.error('Erreur génération acte:', err);
      alert("Erreur lors de la communication avec l'assistant juridique IA.");
    } finally {
      setGenerating(false);
    }
  };

  // Sauvegarde d'acte
  const handleSaveActe = async () => {
    if (!generatedActe.trim() || !acteNom.trim()) return;

    try {
      const res = await api.post('/juridique/actes/sauvegarder', {
        nom_document: acteNom.trim(),
        type_document: selectedTemplate,
        contenu: generatedActe
      });

      if (res && res.success) {
        alert("✅ Acte juridique sauvegardé dans votre espace sécurisé !");
        fetchSavedActes();
      } else {
        alert("Erreur lors de la sauvegarde.");
      }
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      alert("Impossible de sauvegarder le document.");
    }
  };

  // Télécharger en TXT
  const handleDownloadTxt = () => {
    const element = document.createElement("a");
    const file = new Blob([generatedActe], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = acteNom || "document_juridique.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Envoyer un message dans le chat
  const handleSendChatMessage = async (e) => {
    if (e) e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userText = chatInput.trim();
    const newMessages = [...chatMessages, { role: 'user', text: userText }];
    
    setChatMessages(newMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const res = await api.post('/juridique/chat', {
        messages: newMessages,
        contexte_acte: generatedActe || null
      });

      if (res && res.success && res.reponse) {
        setChatMessages(prev => [...prev, { role: 'ai', text: res.reponse }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'ai', text: "Désolé, je n'ai pas pu générer de réponse juridique. Veuillez réessayer." }]);
      }
    } catch (err) {
      console.error('Erreur chat juridique:', err);
      setChatMessages(prev => [...prev, { role: 'ai', text: "Erreur de connexion avec le serveur d'aide juridique." }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Rendu Markdown simplifié pour le chat conseil
  const formatMessageText = (text) => {
    if (!text) return "";
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code style="background:#f1f5f9;padding:2px 4px;border-radius:4px;font-family:monospace;font-size:12px;">$1</code>')
      .replace(/\n/g, '<br />');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', padding: '8px' }}>
      
      {/* Menu à onglets principaux */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px', marginBottom: '20px' }}>
        <button 
          onClick={() => setActiveTab('analyse')}
          style={{
            background: activeTab === 'analyse' ? '#0F6E56' : 'transparent',
            color: activeTab === 'analyse' ? 'white' : '#4B5563',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
        >
          🔍 Analyser un acte (Statuts, PV, Contrats)
        </button>
        <button 
          onClick={() => setActiveTab('generateur')}
          style={{
            background: activeTab === 'generateur' ? '#0F6E56' : 'transparent',
            color: activeTab === 'generateur' ? 'white' : '#4B5563',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
        >
          ✍ Générateur d'Actes & Décisions IA
        </button>
      </div>

      {activeTab === 'analyse' ? (
        <div style={{ display: 'flex', gap: '24px', flex: 1, overflow: 'hidden' }}>
          
          {/* Liste principale et upload */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #E8E7E3', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Analyse Documentaire Juridique</h3>
                <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>Téléversez vos statuts ou procès-verbaux d'AG pour extraire instantanément une synthèse IA structurée.</p>
              </div>
              
              <label style={{
                background: '#0F6E56',
                color: 'white',
                padding: '10px 16px',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.2s'
              }}>
                {uploading ? 'Analyse...' : '⚖ Téléverser un acte'}
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileUpload} disabled={uploading} style={{ display: 'none' }} />
              </label>
            </div>

            {uploading && (
              <div style={{ padding: '24px', background: '#F0FDF4', border: '1px solid #DCFCE7', borderRadius: '12px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '20px', height: '20px', border: '2.5px solid #0F6E56', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>Extraction sémantique & OCR en cours</div>
                  <div style={{ fontSize: '12px', color: '#15803d', marginTop: '2px' }}>Analyse des clauses de l'acte par l'IA...</div>
                </div>
              </div>
            )}

            {loading && syntheses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>Chargement des synthèses...</div>
            ) : syntheses.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #E5E7EB', borderRadius: '16px', padding: '60px 20px', textAlign: 'center' }}>
                <span style={{ fontSize: '40px', marginBottom: '16px' }}>⚖</span>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>Aucun acte analysé</h3>
                <p style={{ fontSize: '13px', color: '#6B7280', maxWidth: '380px', marginTop: '6px' }}>Déposez votre premier document juridique pour obtenir un décryptage immédiat.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {syntheses.map((item) => (
                  <div 
                    key={item.id} 
                    onClick={() => setSelectedSynthese(item)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      borderRadius: '12px',
                      border: `1px solid ${selectedSynthese?.id === item.id ? '#0F6E56' : '#E5E7EB'}`,
                      background: selectedSynthese?.id === item.id ? '#F0FDF9' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '24px' }}>📄</span>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>{item.nom_document}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                          Analysé le {new Date(item.created_at).toLocaleDateString('fr-FR')} • {item.forme_juridique || 'Type inconnu'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {item.capital_social && (
                        <span style={{ background: '#E0F2FE', color: '#0369A1', fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '20px' }}>
                          Cap: {item.capital_social.toLocaleString('fr-FR')} €
                        </span>
                      )}
                      <span style={{ background: '#E1F5EE', color: '#0F6E56', fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '20px' }}>
                        Détails ↗
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Panneau de détails */}
          <div style={{ width: '400px', background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #E8E7E3', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            {selectedSynthese ? (
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px', borderBottom: '1px solid #F3F4F6', paddingBottom: '12px' }}>
                  Fiche de Synthèse IA
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ background: '#FAF9F5', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase' }}>Structure</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginTop: '2px' }}>{selectedSynthese.forme_juridique || 'Non détectée'}</div>
                  </div>
                  <div style={{ background: '#FAF9F5', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase' }}>Clôture Sociale</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginTop: '2px' }}>{selectedSynthese.date_cloture_exercice || 'Non détectée'}</div>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', marginBottom: '6px' }}>Capital Social</h4>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#0F6E56' }}>
                    {selectedSynthese.capital_social ? `${selectedSynthese.capital_social.toLocaleString('fr-FR')} €` : 'Non spécifié'}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', marginBottom: '6px' }}>Gérance & Représentation</h4>
                  <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.4' }}>{selectedSynthese.dirigeants || 'Non spécifiée'}</p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', marginBottom: '6px' }}>Objet Social</h4>
                  <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.4', background: '#FAF9F5', padding: '12px', borderRadius: '8px' }}>
                    {selectedSynthese.objet_social || 'Aucun objet extrait'}
                  </p>
                </div>

                {selectedSynthese.resolutions_cles && selectedSynthese.resolutions_cles.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', marginBottom: '8px' }}>Clauses / Résolutions Extraites</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {selectedSynthese.resolutions_cles.map((r, i) => (
                        <div key={i} style={{ fontSize: '12px', color: '#1e293b', background: '#f1f5f9', padding: '6px 12px', borderRadius: '6px' }}>
                          • {r}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', marginBottom: '6px' }}>Résumé Décrypté</h4>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#374151', 
                    lineHeight: '1.5', 
                    whiteSpace: 'pre-line',
                    background: '#FAF9F5', 
                    padding: '12px', 
                    borderRadius: '8px',
                    borderLeft: '4px solid #0F6E56'
                  }}>
                    {selectedSynthese.synthese}
                  </div>
                </div>

              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', textAlign: 'center' }}>
                <span style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</span>
                <p style={{ fontSize: '13px', margin: 0 }}>Sélectionnez un acte pour afficher sa fiche de synthèse juridique.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ONGLET GÉNÉRATEUR D'ACTES */
        <div style={{ display: 'flex', gap: '24px', flex: 1, overflow: 'hidden' }}>
          {/* Workspace gauche : sélecteur et options */}
          <div style={{ flex: 1.2, background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #E8E7E3', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 16px' }}>Assistant de Rédaction d'Actes & Décisions</h3>
            
            {/* Template selector grids */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              {[
                { id: 'non_remuneration', icon: '⚖', title: 'Non-rémunération', desc: 'Décision ou PV constatant le mandat sans salaire.' },
                { id: 'siege_social', icon: '🏢', title: 'Transfert de Siège', desc: 'Modification de l\'adresse statutaire.' },
                { id: 'approbation_comptes', icon: '📊', title: 'Approbation des Comptes', desc: 'AGO annuelle obligatoire d\'exercice clos.' },
                { id: 'capital', icon: '💰', title: 'Variation de Capital', desc: 'PV d\'augmentation du capital social.' },
                { id: 'custom', icon: '✍', title: 'Acte personnalisé libre', desc: 'Rédigez un contrat ou document sur mesure.' }
              ].map(item => (
                <div 
                  key={item.id} 
                  onClick={() => {
                    setSelectedTemplate(item.id);
                    setGeneratedActe('');
                  }}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${selectedTemplate === item.id ? '#0F6E56' : '#E5E7EB'}`,
                    background: selectedTemplate === item.id ? '#F0FDF9' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '13px', color: selectedTemplate === item.id ? '#0F6E56' : '#1F2937' }}>
                    <span>{item.icon}</span> {item.title}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>{item.desc}</div>
                </div>
              ))}
            </div>

            {/* Formulaire des paramètres contextuels */}
            <form onSubmit={handleGenerate} style={{ display: 'flex', flexDirection: 'column', gap: '14px', background: '#FAF9F5', padding: '16px', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Paramètres du document</h4>
              
              {selectedTemplate === 'siege_social' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#4B5563', fontWeight: '500' }}>Nouvelle adresse complète du siège social :</label>
                  <input 
                    type="text" 
                    placeholder="Ex: 45 rue de la Paix, 75002 Paris"
                    value={customParams.nouveau_siege}
                    onChange={(e) => setCustomParams(prev => ({...prev, nouveau_siege: e.target.value}))}
                    required
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>
              )}

              {selectedTemplate === 'approbation_comptes' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#4B5563', fontWeight: '500' }}>Date de clôture de l'exercice concerné :</label>
                  <input 
                    type="text" 
                    placeholder="Ex: 31 décembre 2025"
                    value={customParams.exercice_clos}
                    onChange={(e) => setCustomParams(prev => ({...prev, exercice_clos: e.target.value}))}
                    required
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>
              )}

              {selectedTemplate === 'capital' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#4B5563', fontWeight: '500' }}>Nouveau capital social total (Euros) :</label>
                  <input 
                    type="text" 
                    placeholder="Ex: 50 000"
                    value={customParams.capital_nouveau}
                    onChange={(e) => setCustomParams(prev => ({...prev, capital_nouveau: e.target.value}))}
                    required
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: '#4B5563', fontWeight: '500' }}>
                  {selectedTemplate === 'custom' ? 'Décrivez précisément le document à rédiger :' : 'Consignes particulières ou ajouts optionnels :'}
                </label>
                <textarea 
                  rows={selectedTemplate === 'custom' ? 5 : 3}
                  placeholder={selectedTemplate === 'custom' ? 'Ex: Rédige un contrat de prestation de services de conseil en informatique d\'une durée de 6 mois entre Anges Mobilité et la société ABC...' : 'Ex: Spécifier que la décision prend effet rétroactivement...'}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  required={selectedTemplate === 'custom'}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'sans-serif' }}
                />
              </div>

              <button 
                type="submit" 
                disabled={generating}
                style={{
                  background: '#0F6E56',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  marginTop: '6px'
                }}
              >
                {generating ? '✍ Rédaction de l\'acte par l\'IA en cours...' : '✍ Lancer la rédaction automatique'}
              </button>
            </form>

            {/* Zone de prévisualisation et éditeur de texte */}
            {(generatedActe || generating) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid #E5E7EB', paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>Brouillon généré (Éditable) :</span>
                  {generatedActe && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(generatedActe);
                          alert('✅ Document copié !');
                        }}
                        style={{ background: '#FAF9F5', border: '1px solid #CBD5E1', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}
                      >
                        📋 Copier
                      </button>
                      <button 
                        onClick={handleDownloadTxt}
                        style={{ background: '#FAF9F5', border: '1px solid #CBD5E1', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}
                      >
                        📥 Télécharger
                      </button>
                      <button 
                        onClick={handleSaveActe}
                        style={{ background: '#E1F5EE', border: '1px solid #A7F3D0', color: '#0F6E56', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        💾 Sauvegarder
                      </button>
                    </div>
                  )}
                </div>

                {generating ? (
                  <div style={{ height: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0', color: '#64748B' }}>
                    <div style={{ width: '32px', height: '32px', border: '3px solid #0F6E56', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
                    <span>L\'avocat IA rédige le document juridique sur mesure...</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#4B5563' }}>Nom de l'acte :</label>
                      <input 
                        type="text"
                        value={acteNom}
                        onChange={(e) => setActeNom(e.target.value)}
                        style={{ flex: 1, padding: '4px 8px', border: '1px solid #CBD5E1', borderRadius: '4px', fontSize: '12px', outline: 'none' }}
                      />
                    </div>
                    <textarea
                      value={generatedActe}
                      onChange={(e) => setGeneratedActe(e.target.value)}
                      rows={18}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid #CBD5E1',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        lineHeight: '1.6',
                        outline: 'none',
                        resize: 'vertical',
                        background: '#FAF9F5',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Workspace droit : Historique commutateur (Actes Sauvegardés VS Chat Conseils) */}
          <div style={{ width: '380px', background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #E8E7E3', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            
            {/* Toggles */}
            <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', marginBottom: '16px', paddingBottom: '4px', gap: '8px' }}>
              <button 
                onClick={() => setRightTab('actes')}
                style={{
                  flex: 1,
                  background: rightTab === 'actes' ? '#FAF9F5' : 'transparent',
                  border: 'none',
                  borderBottom: rightTab === 'actes' ? '2.5px solid #0F6E56' : '2.5px solid transparent',
                  padding: '8px 4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: rightTab === 'actes' ? '#0F6E56' : '#6B7280',
                  transition: 'all 0.15s'
                }}
              >
                📁 Actes Sauvegardés
              </button>
              <button 
                onClick={() => setRightTab('chat')}
                style={{
                  flex: 1,
                  background: rightTab === 'chat' ? '#FAF9F5' : 'transparent',
                  border: 'none',
                  borderBottom: rightTab === 'chat' ? '2.5px solid #0F6E56' : '2.5px solid transparent',
                  padding: '8px 4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: rightTab === 'chat' ? '#0F6E56' : '#6B7280',
                  transition: 'all 0.15s'
                }}
              >
                💬 Tchat Assistant Conseils
              </button>
            </div>

            {/* Contenu de l'onglet : Actes Sauvegardés */}
            {rightTab === 'actes' ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                {loadingActes && savedActes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#6B7280', fontSize: '13px' }}>Chargement des actes...</div>
                ) : savedActes.length === 0 ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', textAlign: 'center', padding: '20px' }}>
                    <span style={{ fontSize: '28px', marginBottom: '10px' }}>📁</span>
                    <p style={{ fontSize: '12px', margin: 0 }}>Aucun acte enregistré dans votre coffre-fort pour le moment.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {savedActes.map((acte) => (
                      <div 
                        key={acte.id}
                        onClick={() => {
                          setSelectedSavedActe(acte);
                          setGeneratedActe(acte.contenu);
                          setActeNom(acte.nom_document);
                          setSelectedTemplate(acte.type_document);
                        }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: `1px solid ${selectedSavedActe?.id === acte.id ? '#0F6E56' : '#E5E7EB'}`,
                          background: selectedSavedActe?.id === acte.id ? '#F0FDF9' : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1F2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {acte.nom_document}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px', display: 'flex', justifyContent: 'space-between' }}>
                          <span>Type: {acte.type_document === 'non_remuneration' ? 'Non-Rémun.' : acte.type_document === 'siege_social' ? 'Siège' : acte.type_document === 'approbation_comptes' ? 'Accounts' : 'Custom'}</span>
                          <span>{new Date(acte.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Contenu de l'onglet : Tchat Assistant Conseils */
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                
                {/* Zone des messages */}
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '12px', paddingRight: '4px' }}>
                  {chatMessages.map((msg, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '85%',
                        background: msg.role === 'user' ? '#0F6E56' : '#F3F4F6',
                        color: msg.role === 'user' ? 'white' : '#1F2937',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        borderBottomRightRadius: msg.role === 'user' ? '2px' : '12px',
                        borderBottomLeftRadius: msg.role === 'ai' ? '2px' : '12px',
                        fontSize: '12.5px',
                        lineHeight: '1.5',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: formatMessageText(msg.text) }} />
                    </div>
                  ))}
                  
                  {chatLoading && (
                    <div style={{ 
                      alignSelf: 'flex-start', 
                      background: '#F3F4F6', 
                      padding: '10px 14px', 
                      borderRadius: '12px', 
                      borderBottomLeftRadius: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#0F6E56', animation: 'bounce 1s infinite' }}></span>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#0F6E56', animation: 'bounce 1s 0.2s infinite' }}></span>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#0F6E56', animation: 'bounce 1s 0.4s infinite' }}></span>
                    </div>
                  )}
                  
                  <div ref={chatEndRef} />
                </div>

                {/* Formulaire d'envoi */}
                <form onSubmit={handleSendChatMessage} style={{ borderTop: '1px solid #E5E7EB', paddingTop: '10px', display: 'flex', gap: '6px' }}>
                  <input 
                    type="text" 
                    placeholder="Posez votre question juridique..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    disabled={chatLoading}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #CBD5E1',
                      borderRadius: '8px',
                      fontSize: '12.5px',
                      outline: 'none',
                      background: '#FAF9F5'
                    }}
                  />
                  <button 
                    type="submit"
                    disabled={chatLoading || !chatInput.trim()}
                    style={{
                      background: '#0F6E56',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0 14px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: (!chatInput.trim() || chatLoading) ? 0.6 : 1
                    }}
                  >
                    Envoyer
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
};

export default Juridique;
